import { quoteDiscount } from './QuoteDiscount'
import { quoteMargin } from './QuoteMargin'
import type { QuoteBlock, QuoteTotals } from './QuoteTypes'

/**
 * QuoteCalculator — quantities, purchase/sale, profit, margin, discounts, total.
 */
export class QuoteCalculator {
  recalculateBlock(block: QuoteBlock): QuoteBlock {
    if (!isPriced(block)) {
      return {
        ...block,
        margin: 0,
        profit: 0,
        salePrice: block.type === 'discount' ? block.salePrice : block.salePrice,
      }
    }
    const margin = quoteMargin.percent(block.purchasePrice, block.salePrice)
    const profit = quoteMargin.profit(block.purchasePrice, block.salePrice, block.quantity)
    return { ...block, margin, profit }
  }

  totals(
    blocks: QuoteBlock[],
    globalDiscountPercent = 0,
    globalDiscountValue = 0,
  ): QuoteTotals {
    const priced = blocks.map((b) => this.recalculateBlock(b)).filter(isPriced)

    const quantity = priced.reduce((s, b) => s + b.quantity, 0)
    const purchaseTotal = round2(
      priced.reduce((s, b) => s + b.purchasePrice * b.quantity, 0),
    )
    const saleGross = round2(priced.reduce((s, b) => s + b.salePrice * b.quantity, 0))
    const itemDiscounts = round2(
      priced.reduce((s, b) => s + quoteDiscount.lineDiscountAmount(b), 0),
    )
    const afterItemDiscount = round2(saleGross - itemDiscounts)
    const globalDiscount = quoteDiscount.globalDiscountAmount(
      afterItemDiscount,
      globalDiscountPercent,
      globalDiscountValue,
    )
    const discountTotal = round2(itemDiscounts + globalDiscount)
    const total = round2(Math.max(0, afterItemDiscount - globalDiscount))
    const profitTotal = round2(total - purchaseTotal)
    const marginPercent = total > 0 ? round2((profitTotal / total) * 100) : 0

    return {
      quantity,
      purchaseTotal,
      saleTotal: saleGross,
      profitTotal,
      marginPercent,
      discountTotal,
      total,
    }
  }
}

function isPriced(block: QuoteBlock): boolean {
  return block.type === 'product' || block.type === 'service' || block.type === 'labor'
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const quoteCalculator = new QuoteCalculator()
